import { useEffect, useRef } from 'react';

const DISPLAY_PALETTE = [
  '#1f2937', // 0 = vuoto/sfondo
  '#ef4444', // 1 = rosso
  '#3b82f6', // 2 = blu
  '#22c55e', // 3 = verde
  '#eab308', // 4 = giallo
  '#a855f7', // 5 = viola
  '#f97316', // 6 = arancione
  '#ec4899', // 7 = rosa
  '#06b6d4', // 8 = ciano
];

interface ChallengeResultDisplayProps {
  challengeId: string;
  variant: 'mole' | 'balance' | 'pixelart' | 'explore';
  result: 'success' | 'failure';
  finalScore: number;
  targetScore: number;
  whiteClicks?: number;
  blackClicks?: number;
  averageAccuracy?: number;
  collectiveAccuracy?: number;
  collectiveGrid?: number[][];
  targetGrid?: number[][];
}

interface ConfettiPiece {
  x: number;
  y: number;
  rotation: number;
  color: string;
  size: number;
  speedY: number;
  speedX: number;
  speedRotation: number;
}

function ChallengeResultDisplay({
  result,
  finalScore,
  targetScore,
  variant,
  whiteClicks,
  blackClicks,
  collectiveAccuracy,
  collectiveGrid,
  targetGrid,
}: ChallengeResultDisplayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const confettiRef = useRef<ConfettiPiece[]>([]);
  const animationRef = useRef<number>(0);

  // Confetti per successo
  useEffect(() => {
    if (result !== 'success') return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const colors = [
      '#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff',
      '#ff8800', '#ff0088', '#88ff00', '#0088ff', '#8800ff', '#00ff88',
    ];

    const pieces: ConfettiPiece[] = [];
    for (let i = 0; i < 200; i++) {
      pieces.push({
        x: Math.random() * canvas.width,
        y: -20 - Math.random() * canvas.height,
        rotation: Math.random() * 360,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 12 + Math.random() * 16,
        speedY: 2 + Math.random() * 5,
        speedX: -3 + Math.random() * 6,
        speedRotation: -5 + Math.random() * 10,
      });
    }
    confettiRef.current = pieces;

    const animate = () => {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      confettiRef.current.forEach((piece) => {
        piece.y += piece.speedY;
        piece.x += piece.speedX;
        piece.rotation += piece.speedRotation;

        if (piece.y > canvas.height + 20) {
          piece.y = -20;
          piece.x = Math.random() * canvas.width;
        }

        ctx.save();
        ctx.translate(piece.x, piece.y);
        ctx.rotate((piece.rotation * Math.PI) / 180);
        ctx.fillStyle = piece.color;
        ctx.fillRect(-piece.size / 2, -piece.size / 4, piece.size, piece.size / 2);
        ctx.restore();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationRef.current);
    };
  }, [result]);

  if (result === 'success') {
    return (
      <div className="h-screen w-screen display-dark-bg display-dark-bg-green flex items-center justify-center relative overflow-hidden">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: 10 }}
        />

        <div className="relative z-20 text-center">
          <h1
            className="text-[8rem] font-black text-parchment-100 font-display leading-none mb-8"
            style={{ textShadow: '0 0 60px rgba(34, 197, 94, 0.4)' }}
          >
            PROVA SUPERATA!
          </h1>
          {variant === 'pixelart' ? (
            <div>
              <div className="text-6xl text-green-300 font-heading font-bold mb-6">
                {collectiveAccuracy !== undefined ? Math.round(collectiveAccuracy) : 0}%
              </div>
              {targetGrid && collectiveGrid && (
                <div className="flex items-center justify-center gap-8 mt-4">
                  <div>
                    <div className="text-parchment-400 font-heading text-2xl mb-2">Obiettivo</div>
                    <div className="grid gap-[1px] bg-gray-700 p-[1px] rounded" style={{ gridTemplateColumns: 'repeat(10, 1fr)', width: 200, height: 200 }}>
                      {targetGrid.map((row, r) => row.map((cell, c) => (
                        <div key={`t-${r}-${c}`} style={{ backgroundColor: DISPLAY_PALETTE[cell] || '#1f2937' }} />
                      )))}
                    </div>
                  </div>
                  <div>
                    <div className="text-parchment-400 font-heading text-2xl mb-2">Risultato</div>
                    <div className="grid gap-[1px] bg-gray-700 p-[1px] rounded" style={{ gridTemplateColumns: 'repeat(10, 1fr)', width: 200, height: 200 }}>
                      {collectiveGrid.map((row, r) => row.map((cell, c) => (
                        <div key={`c-${r}-${c}`} style={{ backgroundColor: DISPLAY_PALETTE[cell] || '#1f2937' }} />
                      )))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : variant === 'mole' || variant === 'explore' ? (
            <div className="text-6xl text-green-300 font-heading font-bold mb-4">
              {Math.min(100, Math.round((finalScore / targetScore) * 100))}%
            </div>
          ) : (
            <>
              <div className="text-6xl text-green-300 font-heading font-bold mb-4">
                {finalScore} / {targetScore}
              </div>
              {whiteClicks !== undefined && blackClicks !== undefined && (
                <div className="flex gap-12 justify-center text-3xl mt-6">
                  <span className="text-red-400">Rosso: {whiteClicks}</span>
                  <span className="text-green-400">Verde: {blackClicks}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // Fallimento - stile GTA Wasted
  return (
    <div className="h-screen w-screen bg-gray-900 flex items-center justify-center relative overflow-hidden">
      {/* Overlay rosso desaturato */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, rgba(139, 0, 0, 0.3), rgba(50, 0, 0, 0.5))',
        }}
      />

      <div className="relative z-20 text-center">
        <h1
          className="text-[8rem] font-black text-red-600 leading-none mb-8 tracking-wider"
          style={{
            textShadow: '0 0 40px rgba(220, 38, 38, 0.5), 0 0 80px rgba(220, 38, 38, 0.3)',
            animation: 'wastedZoom 0.8s ease-out forwards',
          }}
        >
          PROVA FALLITA
        </h1>
        {variant === 'pixelart' ? (
          <div>
            <div className="text-4xl text-gray-500 font-bold mb-4">
              {collectiveAccuracy !== undefined ? Math.round(collectiveAccuracy) : 0}%
            </div>
            {targetGrid && collectiveGrid && (
              <div className="flex items-center justify-center gap-8 mt-4">
                <div>
                  <div className="text-white/30 text-xl mb-1">Obiettivo</div>
                  <div className="grid gap-[1px] bg-gray-700 p-[1px] rounded" style={{ gridTemplateColumns: 'repeat(10, 1fr)', width: 160, height: 160 }}>
                    {targetGrid.map((row, r) => row.map((cell, c) => (
                      <div key={`t-${r}-${c}`} style={{ backgroundColor: DISPLAY_PALETTE[cell] || '#1f2937' }} />
                    )))}
                  </div>
                </div>
                <div>
                  <div className="text-white/30 text-xl mb-1">Risultato</div>
                  <div className="grid gap-[1px] bg-gray-700 p-[1px] rounded" style={{ gridTemplateColumns: 'repeat(10, 1fr)', width: 160, height: 160 }}>
                    {collectiveGrid.map((row, r) => row.map((cell, c) => (
                      <div key={`c-${r}-${c}`} style={{ backgroundColor: DISPLAY_PALETTE[cell] || '#1f2937' }} />
                    )))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : variant === 'mole' || variant === 'explore' ? (
          <div className="text-4xl text-gray-500 font-bold mb-4">
            {Math.min(100, Math.round((finalScore / targetScore) * 100))}%
          </div>
        ) : (
          <>
            <div className="text-4xl text-gray-500 font-bold mb-4">
              {finalScore} / {targetScore}
            </div>
            {whiteClicks !== undefined && blackClicks !== undefined && (
              <div className="flex gap-12 justify-center text-2xl mt-6">
                <span className="text-red-400/70">Rosso: {whiteClicks}</span>
                <span className="text-green-400/70">Verde: {blackClicks}</span>
                <span className="text-red-400/70">Diff: {Math.abs(whiteClicks - blackClicks)}</span>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes wastedZoom {
          0% { transform: scale(3); opacity: 0; }
          60% { transform: scale(0.9); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default ChallengeResultDisplay;
